import React from 'react';
import { Star, Stars, Sparkles, ArrowLeft, ArrowRight } from 'lucide-react';

interface FinishingStepProps {
  value: {
    finishingLevel: 'economic' | 'standard' | 'premium' | null;
  };
  onChange: (field: string, value: any) => void;
  onNext: () => void;
  onBack: () => void;
}

export const FinishingStep: React.FC<FinishingStepProps> = ({
  value,
  onChange,
  onNext,
  onBack
}) => {
  const handleLevelSelect = (level: 'economic' | 'standard' | 'premium') => {
    onChange('finishingLevel', level);
  };

  return (
    <div className="space-y-6">
      {/* Boutons de navigation compacts en haut */}
      <div className="flex justify-between items-center pb-4 border-b border-neutral-200">
        <h2 className="text-base font-semibold text-primary">Niveau de finition</h2>
        <div className="flex items-center gap-2 ml-4">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="px-3 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors flex items-center gap-1"
            >
              <ArrowLeft className="h-4 w-4" />
              Précédent
            </button>
          )}
          <button
            type="button"
            onClick={onNext}
            disabled={!value.finishingLevel}
            className={`px-3 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors flex items-center gap-1 ${
              !value.finishingLevel ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            Suivant
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        <FinishingCard
          icon={<Star className="h-8 w-8" />}
          type="economic"
          label="Économique"
          description="Matériaux basiques, finitions simples"
          selected={value.finishingLevel === 'economic'}
          onClick={() => handleLevelSelect('economic')}
        />
        <FinishingCard
          icon={<Stars className="h-8 w-8" />}
          type="standard"
          label="Standard"
          description="Bon rapport qualité/prix"
          selected={value.finishingLevel === 'standard'}
          onClick={() => handleLevelSelect('standard')}
        />
        <FinishingCard
          icon={<Sparkles className="h-8 w-8" />}
          type="premium"
          label="Haut de gamme"
          description="Matériaux nobles, finitions soignées"
          selected={value.finishingLevel === 'premium'}
          onClick={() => handleLevelSelect('premium')}
        />
      </div>
    </div>
  );
};

interface FinishingCardProps {
  icon: React.ReactNode;
  type: string;
  label: string;
  description: string;
  selected: boolean;
  onClick: () => void;
}

const FinishingCard: React.FC<FinishingCardProps> = ({
  icon,
  label,
  description,
  selected,
  onClick
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`
      card flex flex-col items-center p-6 transition-all
      ${selected ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-neutral-50'}
    `}
  >
    <div className={`mb-4 ${selected ? 'text-primary' : 'text-neutral-600'}`}>
      {icon}
    </div>
    <span className={`font-medium mb-2 ${selected ? 'text-primary' : 'text-neutral-700'}`}>
      {label}
    </span>
    <p className="text-sm text-neutral-500 text-center">
      {description}
    </p>
  </button>
);

export default FinishingStep;